import * as React from "react";
import { Button } from "./ui/button";
import type { ButtonProps as ShadcnButtonProps } from "./ui/button";
import { cn } from "@/app/_utils/cn";
import LoadingDots from "./LoadingDots";

export interface CuoButtonProps extends Omit<ShadcnButtonProps, "size"> {
  label?: React.ReactNode;
  icon?: React.ReactNode;
  disableOnAsync?: boolean;
  buttonColor?: string;
  textColor?: string;
  size?: ShadcnButtonProps["size"] | "small";
  /** MUI-era no-op, kept so existing call sites don't need touching. */
  disableRipple?: boolean;
}

const CuoButton = React.forwardRef<HTMLButtonElement, CuoButtonProps>(
  (props, ref) => {
    const {
      label,
      icon,
      disableOnAsync,
      disabled,
      buttonColor,
      textColor,
      variant = "default",
      size = "default",
      className,
      style,
      children,
      disableRipple: _disableRipple,
      ...rest
    } = props;

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size === "small" ? "sm" : size}
        disabled={disableOnAsync || disabled}
        className={cn(
          "rounded-md font-normal",
          size !== "sm" && "h-[30px] px-3",
          disableOnAsync && "relative",
          className
        )}
        style={{
          ...(buttonColor && { background: buttonColor, borderColor: buttonColor }),
          ...(textColor && { color: textColor }),
          ...style,
        }}
        {...rest}
      >
        {/* Kept mounted but invisible so the button holds its width while loading. */}
        <span
          className={cn(
            "inline-flex items-center gap-2",
            disableOnAsync && "invisible"
          )}
          aria-hidden={disableOnAsync || undefined}
        >
          {icon}
          {label}
          {children}
        </span>
        {disableOnAsync && (
          <LoadingDots className="absolute inset-0 justify-center" />
        )}
      </Button>
    );
  }
);
CuoButton.displayName = "CuoButton";

export default CuoButton;
